import * as XLSX from 'xlsx';
import { logError } from './errorLogger';

/**
 * Utility functions for Excel export functionality
 */
export class ExcelExportUtils {
  /**
   * Export data to Excel file with enhanced formatting
   */
  static exportToExcel(
    data: any[], 
    filename: string, 
    sheetName: string = 'Sheet1',
    options?: {
      autoWidth?: boolean;
      freezeHeader?: boolean;
      addFilters?: boolean;
    }
  ): void {
    try {
      // Create a new workbook
      const workbook = XLSX.utils.book_new();
      
      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Apply formatting options
      if (options?.autoWidth) {
        this.autoSizeColumns(worksheet);
      }
      
      if (options?.freezeHeader) {
        worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
      }
      
      if (options?.addFilters) {
        worksheet['!autofilter'] = { ref: `A1:${String.fromCharCode(65 + Object.keys(data[0] || {}).length - 1)}${data.length + 1}` };
      }
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      // Generate Excel file and trigger download
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      logError('Error exporting to Excel', error);
      throw new Error('Failed to export to Excel');
    }
  }

  /**
   * Auto-size columns based on content
   */
  private static autoSizeColumns(worksheet: XLSX.WorkSheet): void {
    const colWidths: number[] = [];
    
    // Calculate max width for each column
    const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
    sheetData.forEach((row: any[]) => {
      row.forEach((cell: any, index: number) => {
        if (cell) {
          const cellLength = String(cell).length;
          colWidths[index] = Math.max(colWidths[index] || 0, cellLength);
        }
      });
    });
    
    // Set column widths (with some padding and limits)
    worksheet['!cols'] = colWidths.map(width => ({
      wch: Math.min(Math.max(width + 2, 10), 50) // Min 10, Max 50 characters
    }));
  }

  /**
   * Format guest data for Excel export (with companions as additional columns)
   */
  static formatGuestDataForExport(guestData: any[]): any[] {
    // Find the maximum number of companions any guest has
    const maxCompanions = Math.max(
      0,
      ...guestData.map(guest => (guest.companions || []).length)
    );
    
    return guestData.map(guest => {
      // Start with main guest columns
      const row: any = {
        'First Name': guest.first_name || '',
        'Last Name': guest.last_name || '',
        'Phone': guest.phone || '',
        'Email': guest.email || '',
        'Status': this.formatStatus(guest.status),
        'Companion Count': guest.companion_count || 0,
        'Invited At': guest.invited_at ? this.formatDate(guest.invited_at) : '',
        'Responded At': guest.responded_at ? this.formatDate(guest.responded_at) : '',
        'Source': guest.is_imported ? 'Imported' : 'Manual'
      };
      
      // Add companion columns dynamically based on maxCompanions
      const companions = guest.companions || [];
      for (let i = 0; i < maxCompanions; i++) {
        const companion = companions[i];
        const num = i + 1;
        
        if (companion) {
          row[`Companion ${num} Name`] = companion.name || '';
          row[`Companion ${num} Phone`] = companion.phone || '';
          row[`Companion ${num} Relationship`] = companion.relationship ? this.formatRelationship(companion.relationship) : '';
        } else {
          // Empty columns for guests with fewer companions
          row[`Companion ${num} Name`] = '';
          row[`Companion ${num} Phone`] = '';
          row[`Companion ${num} Relationship`] = '';
        }
      }
      
      return row;
    });
  }

  /**
   * Format status for display
   */
  private static formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'pending': 'Pending',
      'accepted': 'Confirmed',
      'rejected': 'Declined',
      'ignored': 'No Response'
    };
    return statusMap[status] || status;
  }

  /**
   * Format relationship for display
   */
  private static formatRelationship(relationship: string): string {
    const relationshipMap: Record<string, string> = {
      'spouse': 'Spouse',
      'partner': 'Partner',
      'child': 'Child',
      'parent': 'Parent',
      'sibling': 'Sibling',
      'friend': 'Friend',
      'colleague': 'Colleague',
      'relative': 'Relative',
      'other': 'Other'
    };
    return relationshipMap[relationship] || relationship;
  }

  /**
   * Format date for display
   */
  private static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Generate filename with timestamp
   */
  static generateFilename(prefix: string, extension: string = 'xlsx'): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    return `${prefix}_${timestamp}.${extension}`;
  }
}
